import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { Settings as SettingsIcon, User, Bell, Shield, Palette, Wallet, Loader2, Save, TrendingUp, RotateCcw, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useWallet } from "@/contexts/WalletContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { ThemeSwitcher } from "@/components/ui/theme-switcher";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export default function Settings() {
  const { connected, address, balance, connect, disconnect, publicKey } = useWallet();
  const { preferences, updatePreference, resetPreferences, isLoaded } = useUserPreferences();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [profile, setProfile] = useState({
    username: "",
    bio: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateUsername = (value: string) => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, username: '' }));
      return true;
    }
    if (value.length < 2) {
      setErrors(prev => ({ ...prev, username: 'Username must be at least 2 characters' }));
      return false;
    }
    if (value.length > 32) {
      setErrors(prev => ({ ...prev, username: 'Username must be less than 32 characters' }));
      return false;
    }
    setErrors(prev => ({ ...prev, username: '' }));
    return true;
  };

  const validateBio = (value: string) => {
    if (value.length > 160) {
      setErrors(prev => ({ ...prev, bio: 'Bio must be less than 160 characters' }));
      return false;
    }
    setErrors(prev => ({ ...prev, bio: '' }));
    return true;
  };

  // Load profile data
  useEffect(() => {
    if (!publicKey) return;

    const loadProfile = async () => {
      setIsLoading(true);
      try {
        const { data } = await supabase
          .from("profiles")
          .select("*")
          .eq("wallet_address", publicKey)
          .maybeSingle();

        if (data) {
          setProfile({
            username: data.username ?? "",
            bio: data.bio ?? "",
          });
        }
      } catch (error) {
        console.error("Failed to load profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [publicKey]);

  const handleSaveProfile = async () => {
    if (!publicKey) return;

    const usernameValid = validateUsername(profile.username);
    const bioValid = validateBio(profile.bio);
    if (!usernameValid || !bioValid) {
      toast({ title: "Invalid Profile", description: "Please fix the validation errors before saving.", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const { data: existing } = await supabase
        .from("profiles")
        .select("wallet_address")
        .eq("wallet_address", publicKey)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("profiles")
          .update({
            username: profile.username.trim() || null,
            bio: profile.bio.trim() || null,
          })
          .eq("wallet_address", publicKey);
      } else {
        await supabase.from("profiles").insert({
          wallet_address: publicKey,
          username: profile.username.trim() || null,
          bio: profile.bio.trim() || null,
        });
      }

      toast({ title: "Profile Saved", description: "Your profile has been updated" });
    } catch (error) {
      console.error("Failed to save profile:", error);
      toast({ title: "Error", description: "Failed to save profile", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPreferences = () => {
    resetPreferences();
    toast({ title: "Preferences Reset", description: "All preferences restored to defaults" });
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings - TheMoonRun</title>
        <meta name="description" content="Manage your TheMoonRun account settings and preferences." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <div className="flex">
          <Sidebar />

          <main className="flex-1 min-w-0 p-4 lg:p-6">
            <div className="max-w-2xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <SettingsIcon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-display font-bold text-foreground">Settings</h1>
                    <p className="text-muted-foreground">Manage your preferences</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleResetPreferences}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All
                </Button>
              </div>

              <div className="space-y-6">
                {/* Wallet Section */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <Wallet className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Wallet</h2>
                  </div>

                  {connected ? (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-secondary/50">
                        <p className="text-sm text-muted-foreground mb-1">Connected Wallet</p>
                        <p className="font-mono text-foreground break-all">{publicKey}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-secondary/50">
                        <p className="text-sm text-muted-foreground mb-1">Balance</p>
                        <p className="font-mono text-2xl font-bold text-foreground">{balance.toFixed(4)} SOL</p>
                      </div>
                      <Button variant="outline" onClick={disconnect} className="w-full">
                        Disconnect Wallet
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-muted-foreground mb-4">No wallet connected</p>
                      <Button variant="hero" onClick={connect}>
                        <Wallet className="w-4 h-4" />
                        Connect Wallet
                      </Button>
                    </div>
                  )}
                </section>

                {/* Profile Section */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <User className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Profile</h2>
                  </div>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="username" className="mb-2 block">Username</Label>
                        <Input
                          id="username"
                          placeholder="Enter username"
                          value={profile.username}
                          onChange={(e) => setProfile(p => ({ ...p, username: e.target.value }))}
                          onBlur={(e) => validateUsername(e.target.value)}
                          disabled={!connected}
                          maxLength={32}
                          aria-invalid={!!errors.username}
                          aria-describedby={errors.username ? "username-error" : undefined}
                          className={errors.username ? "border-destructive" : ""}
                        />
                        {errors.username !== undefined && (
                          <p id="username-error" className={`text-xs mt-1 flex items-center gap-1 ${errors.username ? 'text-destructive' : 'text-success'}`}>
                            {errors.username ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                            {errors.username || 'Valid'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="bio" className="mb-2 block">Bio</Label>
                        <Input
                          id="bio"
                          placeholder="Tell us about yourself"
                          value={profile.bio}
                          onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                          onBlur={(e) => validateBio(profile.bio)}
                          disabled={!connected}
                          maxLength={160}
                          aria-invalid={!!errors.bio}
                          aria-describedby={errors.bio ? "bio-error" : undefined}
                          className={errors.bio ? "border-destructive" : ""}
                        />
                        {errors.bio !== undefined && (
                          <p id="bio-error" className={`text-xs mt-1 flex items-center gap-1 ${errors.bio ? 'text-destructive' : 'text-success'}`}>
                            {errors.bio ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                            {errors.bio || 'Valid'}
                          </p>
                        )}
                      </div>
                      <Button onClick={handleSaveProfile} disabled={!connected || isSaving || !!errors.username || !!errors.bio}>
                        {isSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save Profile
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </section>

                {/* Trading Preferences */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Trading</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label className="mb-2 block">Default Buy Amount (SOL)</Label>
                      <div className="flex items-center gap-4">
                        <Slider
                          value={[preferences.defaultBuyAmount]}
                          onValueChange={([value]) => updatePreference("defaultBuyAmount", value)}
                          min={0.01}
                          max={10}
                          step={0.01}
                          className="flex-1"
                        />
                        <span className="w-16 text-right font-mono">{preferences.defaultBuyAmount} SOL</span>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Default Slippage (%)</Label>
                      <div className="flex items-center gap-4">
                        <Slider
                          value={[preferences.defaultSlippage]}
                          onValueChange={([value]) => updatePreference("defaultSlippage", value)}
                          min={0.1}
                          max={5}
                          step={0.1}
                          className="flex-1"
                        />
                        <span className="w-16 text-right font-mono">{preferences.defaultSlippage}%</span>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">Priority Fee Level</Label>
                      <Select
                        value={preferences.priorityFeeLevel}
                        onValueChange={(value: any) => updatePreference("priorityFeeLevel", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None (Slower)</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="turbo">Turbo (Fastest)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="mb-2 block">Chart Style</Label>
                      <Select
                        value={preferences.chartStyle}
                        onValueChange={(value: any) => updatePreference("chartStyle", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="candles">Candlesticks</SelectItem>
                          <SelectItem value="line">Line</SelectItem>
                          <SelectItem value="area">Area</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                {/* Notifications Section */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <Bell className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Trade Alerts</p>
                        <p className="text-sm text-muted-foreground">Get notified on your trades</p>
                      </div>
                      <Switch
                        checked={preferences.tradeAlerts}
                        onCheckedChange={(checked) => updatePreference("tradeAlerts", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Price Alerts</p>
                        <p className="text-sm text-muted-foreground">Notify when coins reach targets</p>
                      </div>
                      <Switch
                        checked={preferences.priceAlerts}
                        onCheckedChange={(checked) => updatePreference("priceAlerts", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">New Launches</p>
                        <p className="text-sm text-muted-foreground">Get notified on new coins</p>
                      </div>
                      <Switch
                        checked={preferences.newLaunches}
                        onCheckedChange={(checked) => updatePreference("newLaunches", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Streamer Alerts</p>
                        <p className="text-sm text-muted-foreground">Follow streamer activity</p>
                      </div>
                      <Switch
                        checked={preferences.streamerAlerts}
                        onCheckedChange={(checked) => updatePreference("streamerAlerts", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Community Alerts</p>
                        <p className="text-sm text-muted-foreground">Updates from communities you follow</p>
                      </div>
                      <Switch
                        checked={preferences.communityAlerts}
                        onCheckedChange={(checked) => updatePreference("communityAlerts", checked)}
                      />
                    </div>
                  </div>
                </section>

                {/* Security Section */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Security & Privacy</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Transaction Confirmations</p>
                        <p className="text-sm text-muted-foreground">Require extra confirmation for trades</p>
                      </div>
                      <Switch
                        checked={preferences.showConfirmations}
                        onCheckedChange={(checked) => updatePreference("showConfirmations", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Hide Balances</p>
                        <p className="text-sm text-muted-foreground">Hide SOL and token balances</p>
                      </div>
                      <Switch
                        checked={preferences.hideBalances}
                        onCheckedChange={(checked) => updatePreference("hideBalances", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Hide Portfolio</p>
                        <p className="text-sm text-muted-foreground">Make your portfolio private</p>
                      </div>
                      <Switch
                        checked={preferences.hidePortfolio}
                        onCheckedChange={(checked) => updatePreference("hidePortfolio", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Anonymous Mode</p>
                        <p className="text-sm text-muted-foreground">Hide your wallet from leaderboards</p>
                      </div>
                      <Switch
                        checked={preferences.anonymousMode}
                        onCheckedChange={(checked) => updatePreference("anonymousMode", checked)}
                      />
                    </div>
                  </div>
                </section>

                {/* Appearance Section */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <Palette className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Appearance</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label className="mb-4 block">Theme Palette</Label>
                      <ThemeSwitcher />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Animations</p>
                        <p className="text-sm text-muted-foreground">Enable UI animations</p>
                      </div>
                      <Switch
                        checked={preferences.animations}
                        onCheckedChange={(checked) => updatePreference("animations", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Sound Effects</p>
                        <p className="text-sm text-muted-foreground">Play sounds on trades</p>
                      </div>
                      <Switch
                        checked={preferences.soundEffects}
                        onCheckedChange={(checked) => updatePreference("soundEffects", checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">Compact Mode</p>
                        <p className="text-sm text-muted-foreground">Reduce spacing for more content</p>
                      </div>
                      <Switch
                        checked={preferences.compactMode}
                        onCheckedChange={(checked) => updatePreference("compactMode", checked)}
                      />
                    </div>
                  </div>
                </section>

                {/* Navigation Customization */}
                <section className="p-6 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <SettingsIcon className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-semibold text-foreground">Navigation</h2>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Customize your navigation by visiting the Dashboard and clicking "Customize" on Quick Links.
                    </p>
                    <Button variant="outline" asChild>
                      <Link to="/dashboard">Go to Dashboard</Link>
                    </Button>
                  </div>
                </section>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}